import React from 'react';

import { useFilterContext } from '../../../contexts/FiltersContext';
import Filter, { BaseSelectionFilter } from '../../../models/Filter';
import type { Value } from '../SelectionFilter/SelectionFilter';

interface SelectionFilterProps {
  arrOptions: Value[];
  filter: BaseSelectionFilter;
  constructor: (...args: any) => Filter;
  extra?: any;
}

export function useSelectionFilter({ arrOptions, filter, constructor, extra }: SelectionFilterProps) {
  // Dependencies
  const { state, handleApplyFilter, handleRemoveFilter } = useFilterContext();

  // State
  const [optionValues, setOptionValues] = React.useState<Value[]>(arrOptions);
  const [openSelectionModal, setOpenSelectionModal] = React.useState(false);
  const [removeFilterOnApply, setRemoveFilterOnApply] = React.useState(false);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  // Effects
  React.useEffect(() => {
    setOptionValues(updatedValuesFromFilter());
    setIsFilterOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  React.useEffect(() => {
    setOptionValues(arrOptions);
  }, [arrOptions]);

  React.useEffect(() => {
    if (removeFilterOnApply) {
      handleRemoveFilter(filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeFilterOnApply, filter]);

  // Derived State
  const getSelectedValues = () => {
    return optionValues.filter((option) => option.selected === true).map((option) => option.value);
  };

  const updatedValuesFromFilter = () => {
    return optionValues?.map((valueItem) => {
      const filterValues = filter?.values;
      valueItem.selected = filterValues?.some((value) => value === valueItem?.value);
      return valueItem;
    });
  };

  // Exposed Functions
  const getFilter: () => Filter | undefined = () => {
    setRemoveFilterOnApply(false);
    const selectedValues = getSelectedValues();
    if (selectedValues?.length === 0) {
      setRemoveFilterOnApply(true);
      return undefined;
    }
    if (filter) {
      filter.values = selectedValues;
      return undefined;
    }
    return constructor(selectedValues, extra ? extra : undefined);
  };

  const onChangeMultiSelectionFnc = (selected: boolean, value: any) => {
    const auxArray = [...optionValues];
    const selectedIndex = optionValues?.findIndex((option) => option.value === value);
    const item = auxArray[selectedIndex];
    item.selected = selected;
    setOptionValues(auxArray);
  };

  const clearAllSelected = () => {
    const auxArray = [...optionValues];
    auxArray.forEach((item) => {
      item.selected = false;
    });
    setOptionValues(auxArray);
  };

  const applyOnClick = () => {
    if (!getFilter) return;
    const filter = getFilter();
    handleApplyFilter(filter);
  };

  const onCancel = () => {
    setOptionValues(updatedValuesFromFilter());
    setIsFilterOpen(false);
  };

  return {
    getFilter,
    optionValues,
    setOptionValues,
    onCancel,
    onChangeMultiSelectionFnc,
    applyOnClick,
    openSelectionModal,
    setOpenSelectionModal,
    getSelectedValues,
    removeFilterOnApply,
    isFilterOpen,
    setIsFilterOpen,
    setRemoveFilterOnApply,
    clearAllSelected,
  };
}
